import { useQuery } from "@tanstack/react-query";
import { addMinutes, formatISO, subMinutes } from "date-fns";

import {
  MetricsNodeStatusCounts,
  MetricsNodeStatusCountsResponse,
} from "src/api/types";
import { DecisionEnvironment } from "src/clients/history_v3";
import { NODE_TYPE } from "src/constants/NodeTypes";
import { getJobRunFlags } from "src/jobs/types";
import {
  aggregateStatusCounts,
  computeRunningTotal,
} from "src/performance/nodeStatusUtils";
import { useNodeStatusCounts } from "src/performance/queries";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { useCurrentJobRun, useNodeRunState } from "src/store/runState/RunState";
import { useGraphStore } from "src/store/StoreProvider";

export const useMetricsQueryData = (
  jobRunId: string,
): MetricsNodeStatusCountsResponse | undefined => {
  const { data } = useQuery<MetricsNodeStatusCountsResponse>({
    queryKey: ["metrics", "node_status_counts", jobRunId],
    queryFn: () => ({ status_counts: {} }),
    enabled: false,
  });
  return data;
};

export const useJobRunStatusCountsPolling = () => {
  const { workspace, flow } = useAuthoringContext();
  const { nodesArray } = useGraphStore();
  const jobRun = useCurrentJobRun();
  const outputNodeId =
    nodesArray.find((n) => n.type === NODE_TYPE.OUTPUT_NODE)?.id ?? "";

  const { isJobRunning, isJobTerminated } = getJobRunFlags(jobRun?.status.type);

  useNodeStatusCounts({
    baseUrl: workspace.base_url ?? "",
    jobRunId: jobRun?.id ?? "",
    flowId: flow.id,
    environment: DecisionEnvironment.TEST,
    fromStartTime: formatISO(
      subMinutes(new Date(jobRun?.created_at ?? new Date()), 60),
    ),
    untilStartTime: formatISO(
      addMinutes(new Date(jobRun?.updated_at ?? new Date()), 60),
    ),
    isRunning: isJobRunning,
    expectedTotal: jobRun?.total_rows_count ?? 0,
    terminatedAt: isJobTerminated ? jobRun?.updated_at : undefined,
    outputNodeId,
  });
};

export const useCompletedJobRunMetrics = () => {
  const { nodesArray } = useGraphStore();
  const jobRun = useCurrentJobRun();
  const outputNodeId =
    nodesArray.find((n) => n.type === NODE_TYPE.OUTPUT_NODE)?.id ?? "";

  const data = useMetricsQueryData(jobRun?.id ?? "");

  if (!data || !jobRun) return undefined;

  const { isJobRunning, isJobTerminated } = getJobRunFlags(jobRun.status.type);
  // We wait for history to finish writing based on these counts from the jobs API
  // so it's important to account for the case of stopped/failed jobs.
  const expectedTotal = isJobTerminated
    ? jobRun.success_rows_count +
      jobRun.failed_rows_count +
      jobRun.ignored_rows_count +
      jobRun.pending_rows_count
    : jobRun.total_rows_count;
  const runningTotal = computeRunningTotal(data, outputNodeId);
  const isComplete =
    !isJobRunning && expectedTotal > 0 && runningTotal >= expectedTotal;

  if (!isComplete) return undefined;

  return { data, outputNodeId };
};

export const useJobRunCountsForNode = (
  nodeId: string,
): MetricsNodeStatusCounts | undefined => {
  const nodeRunState = useNodeRunState(nodeId);
  const isJobRun = nodeRunState?.type === "job-run";
  const metrics = useCompletedJobRunMetrics();

  if (!isJobRun || !metrics) return undefined;
  return metrics.data.status_counts[nodeId];
};

export const useOutputNodeJobRunCounts = ():
  | { success: number; failed: number; ignored: number; total: number }
  | undefined => {
  const metrics = useCompletedJobRunMetrics();
  if (!metrics) return undefined;

  const { totalSuccess, totalFailed, totalIgnored, totalOther, totalPending } =
    aggregateStatusCounts(metrics.data.status_counts, metrics.outputNodeId);

  return {
    success: totalSuccess,
    failed: totalFailed,
    ignored: totalIgnored,
    total:
      totalSuccess + totalFailed + totalIgnored + totalOther + totalPending,
  };
};
